import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Settings } from "lucide-react";
import { hasAnalyticsConsent, setAnalyticsConsent } from "@/utils/analytics";
import { usePublicPageLoading, usePublicPageReady } from "@/components/PublicPageReadiness";

// Use same high-res PNG as header
const TREE_ICON_URL = "/FinalLogoTransparent.webp";

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const pageReady = usePublicPageReady();
  usePublicPageLoading(settingsLoading);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(hasAnalyticsConsent());

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/cms/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleToggleAnalytics = () => {
    const newValue = !analyticsEnabled;
    setAnalyticsConsent(newValue);
    setAnalyticsEnabled(newValue);
  };

  // Default settings if CMS not available
  const defaultSettings = {
    clinic_name: "Growing Together Physio",
    tagline: "Supporting families through every stage of growth with expert physiotherapy care.",
    contact_phone: "07481899242",
    contact_email: "info@growingtogetherphysio.co.uk",
    address: "",
    business_hours: [
      { day: "Monday", hours: "4:30 pm - 7:30 pm" },
      { day: "Tuesday", hours: "4:30 pm - 7:30 pm" },
      { day: "Wednesday", hours: "4:30 pm - 7:30 pm" },
      { day: "Thursday", hours: "4:30 pm - 7:30 pm" },
      { day: "Friday", hours: "Closed" },
      { day: "Saturday", hours: "8:00 am - 6:00 pm" },
      { day: "Sunday", hours: "8:00 am - 6:00 pm" }
    ],
    social_media: {
      instagram: "https://www.instagram.com/growing_together_physiotherapy/?utm_source=ig_web_button_share_sheet",
      facebook: "https://www.facebook.com/profile.php/?id=61573403706444"
    }
  };

  // Override CMS settings with correct branding where needed
  const raw = settings || defaultSettings;
  const s = {
    ...raw,
    clinic_name: raw.clinic_name?.includes("Thrive") ? defaultSettings.clinic_name : raw.clinic_name,
    tagline: raw.tagline?.toLowerCase().includes("thrive") ? defaultSettings.tagline : raw.tagline,
    contact_email: raw.contact_email?.includes("thrivephysio") ? defaultSettings.contact_email : raw.contact_email,
    social_media: {
      instagram: "https://www.instagram.com/growing_together_physiotherapy/?utm_source=ig_web_button_share_sheet",
      facebook: "https://www.facebook.com/profile.php/?id=61573403706444"
    },
  };

  return (
    <footer className="bg-[#2D4A3E] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {/* Brand Column — constrained to its grid cell */}
          <div className="min-w-0 space-y-4">
            <div className="flex items-center gap-3 mb-6 max-w-[220px]">
              <img 
                src={TREE_ICON_URL} 
                alt="" 
                className="flex-shrink-0"
                style={{ height: '56px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
              />
              <div className="flex flex-col justify-center min-w-0">
                <span 
                  style={{ 
                    fontFamily: "'Playfair Display', serif", 
                    fontWeight: 600,
                    fontSize: '22px',
                    color: '#FFFFFF',
                    lineHeight: 1
                  }}
                >
                  Growing Together
                </span>
                <span 
                  style={{ 
                    fontFamily: "'Inter', sans-serif", 
                    fontWeight: 500,
                    fontSize: '12px',
                    letterSpacing: '2.5px',
                    textTransform: 'uppercase',
                    color: '#A8C5B8',
                    lineHeight: 1,
                    marginTop: '6px'
                  }}
                >
                  PHYSIOTHERAPY
                </span>
              </div>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              {s.tagline || s.description || defaultSettings.tagline}
            </p>
          </div>

          {/* Quick Links — link classes give ≥44 px vertical hit area via py-2.5 */}
          <div>
            <h4 className="font-semibold mb-4 font-['Fraunces']">Quick Links</h4>
            <ul className="space-y-1">
              <li>
                <Link to="/" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors">
                  Our Services
                </Link>
              </li>
              <li className="h-10" data-testid="footer-book-slot">
                {pageReady && <Link to="/booking" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-book-cta">
                  Book Appointment
                </Link>}
              </li>
              <li>
                <Link to="/login" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-staff-login">
                  Staff Login
                </Link>
              </li>
              <li>
                <Link to="/insurance" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-insurance-link">
                  Private Health Insurance
                </Link>
              </li>
              <li>
                <Link to="/services#faqs" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-faqs-link">
                  FAQs
                </Link>
              </li>
              {/* Location pages — internal SEO. Kept compact in Quick Links so the
                  visual layout of the 4-column footer is unchanged. NOT added
                  to the top navigation per requirements. */}
              <li className="pt-2 border-t border-white/10 text-[12px] uppercase tracking-wider text-white/50">Areas we serve</li>
              <li>
                <Link to="/child-physio-sandy" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-loc-sandy">
                  Child Physio Sandy
                </Link>
              </li>
              <li>
                <Link to="/child-physio-bedford" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-loc-bedford">
                  Child Physio Bedford
                </Link>
              </li>
              <li>
                <Link to="/child-physio-hitchin" className="block py-2.5 text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-loc-hitchin">
                  Child Physio Hitchin
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 font-['Fraunces']">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-white/80">
                <Phone className="w-4 h-4 text-[#E09F7D]" />
                <a href={`tel:${(s.contact_phone || "").replace(/\s+/g, "")}`} className="hover:text-white transition-colors" data-testid="footer-phone-link">
                  {s.contact_phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <Mail className="w-4 h-4 text-[#E09F7D]" />
                {s.contact_email}
              </li>
              <li className="flex items-start gap-3 text-sm text-white/80">
                <MapPin className="w-4 h-4 text-[#E09F7D] mt-0.5" />
                <span style={{ whiteSpace: 'pre-line' }}>{s.address}</span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-semibold mb-4 font-['Fraunces']">Opening Hours</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-white/80">
                <Clock className="w-4 h-4 text-[#E09F7D] mt-0.5" />
                <div>
                  {(s.business_hours || defaultSettings.business_hours).map((item, index) => (
                    <p key={index}>{item.day}: {item.hours}</p>
                  ))}
                </div>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a 
                href={s.social_media?.instagram || "#"} 
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={s.social_media?.facebook || "#"} 
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} {s.clinic_name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-white/60 hover:text-white transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-white/60 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/security" className="text-white/60 hover:text-white transition-colors" data-testid="footer-security-link">
              Security &amp; Privacy
            </Link>
            <button 
              onClick={() => setShowCookieSettings(!showCookieSettings)}
              className="text-white/60 hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Settings className="w-3.5 h-3.5" />
              Cookie Settings
            </button>
          </div>
        </div>

        {/* Cookie Settings Panel */}
        {showCookieSettings && (
          <div className="mt-6 p-4 bg-white/10 rounded-xl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h4 className="font-medium text-white mb-1">Cookie Preferences</h4>
                <p className="text-sm text-white/70">
                  We use analytics cookies to understand how you use our website. No advertising or remarketing.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/80">Analytics cookies:</span>
                <button
                  onClick={handleToggleAnalytics}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    analyticsEnabled ? 'bg-[#E09F7D]' : 'bg-white/30'
                  }`}
                  aria-label={analyticsEnabled ? 'Disable analytics' : 'Enable analytics'}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      analyticsEnabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-white/60 w-16">
                  {analyticsEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
